import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Gift, Sparkles, ArrowRight, FileDown } from "lucide-react";
import AdventDoor from "../components/AdventDoor";
import ChristmasCard from "../components/ChristmasCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  "gioco-scoperta": "Gioco e scoperta",
  "relazione-cura": "Relazione e cura",
  "parole-comunicazione": "Parole e comunicazione",
  "autonomia": "Autonomia quotidiana",
};

/** URL assoluto (le immagini caricate vivono sotto /api/uploads/...) */
export const absoluteUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

/** Risolve l'illustrazione di un'ambientazione (1-2-3) dalle impostazioni */
export const adventThemeUrl = (settings, theme) =>
  absoluteUrl(settings?.[`theme${theme}_url`] || "");

function setOgMeta(settings) {
  const cover = absoluteUrl(settings?.cover_image_url);
  const tags = {
    "og:title": settings?.hero_title || "24 giorni per crescere con cura",
    "og:description": settings?.hero_subtitle || "Il nostro Calendario dell'Avvento 2026",
    "og:type": "website",
    "og:url": window.location.href,
  };
  if (cover) {
    tags["og:image"] = cover;
    tags["twitter:card"] = "summary_large_image";
    tags["twitter:image"] = cover;
  }
  Object.entries(tags).forEach(([prop, content]) => {
    let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(prop.startsWith("twitter") ? "name" : "property", prop);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  });
}

export default function Avvento() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.title = "24 giorni per crescere con cura · Teresa Vilardo";
    axios.get(`${API}/advent/days`)
      .then((r) => {
        setData(r.data);
        setOgMeta(r.data?.settings);
      })
      .catch((err) => { if (err.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#3D2140]/50">Un attimo…</div>;
  }
  if (notFound) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-8 text-center">
        <Sparkles className="text-[#FF2768] mb-4" size={32} />
        <h1 className="font-display text-4xl text-[#3D2140] mb-2">Qualcosa di speciale sta arrivando…</h1>
        <p className="text-[#3D2140]/60 max-w-md">Il Calendario dell'Avvento sarà qui dal 25 novembre. Torna a trovarci!</p>
      </div>
    );
  }

  const { settings, days, christmas_card, recap_available } = data;
  const cover = absoluteUrl(settings.cover_image_url);

  // La griglia mostra sempre tutte le 24 caselle: quelle senza contenuto restano chiuse
  const byDay = Object.fromEntries(days.map((d) => [d.day, d]));
  const slots = Array.from({ length: 24 }, (_, i) =>
    byDay[i + 1] || { day: i + 1, unlocked: false, title: "", theme: (i % 3) + 1 }
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAF8F5] to-[#FFE4E8] pb-16" data-testid="avvento-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 pt-10 md:pt-20">
        <div className="text-center mb-10 md:mb-14">          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-semibold mb-4 flex items-center justify-center gap-2">
            <Gift size={14} /> Natale 2026
          </p>

          {cover ? (
            <>
              {/* Copertina ufficiale — proporzioni originali mantenute, nessun taglio */}
              <h1 className="sr-only">{settings.hero_title} — Il nostro Calendario dell'Avvento 2026</h1>
              <motion.img
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                src={cover}
                alt="24 giorni per crescere con cura — Il nostro Calendario dell'Avvento 2026"
                data-testid="avvento-cover"
                className="w-full max-w-3xl mx-auto h-auto rounded-3xl shadow-xl mb-8"
              />
            </>
          ) : (
            <h1 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[1.05] mb-5">
              {settings.hero_title}
            </h1>
          )}

          <p className="font-editorial italic text-lg md:text-xl text-[#FF2768] max-w-3xl mx-auto mb-5">
            {settings.hero_subtitle}
          </p>
          <p className="text-[#3D2140]/75 max-w-2xl mx-auto leading-relaxed">
            {settings.hero_intro}
          </p>
        </div>

        <ReminderSignup />

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2.5 sm:gap-3 md:gap-4" data-testid="avvento-grid">
          {slots.map((d, i) => (
            <motion.div
              key={d.day}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }}
            >
              <AdventDoor
                day={d.day}
                title={d.unlocked ? d.title : ""}
                themeUrl={adventThemeUrl(settings, d.theme)}
                unlocked={d.unlocked}
                testId={d.unlocked ? `avvento-door-${d.day}` : `avvento-door-${d.day}-locked`}
              />
            </motion.div>
          ))}
        </div>

        {christmas_card && (
          <ChristmasCard
            imageUrl={absoluteUrl(christmas_card.image_url)}
            message={christmas_card.message}
          />
        )}

        {recap_available && (
          <div className="text-center mt-14" data-testid="avvento-recap">
            <p className="text-sm text-[#3D2140]/60 mb-3">Il nostro viaggio insieme, da tenere sempre con te</p>
            <a
              href={`${API}/advent/recap.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="avvento-recap-download"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#FF2768] text-white font-semibold text-sm hover:bg-[#c81e52] transition-colors shadow-lg"
            >
              <FileDown size={16} /> Scarica il riepilogo delle 24 attività (PDF)
            </a>
          </div>
        )}

        <div className="text-center mt-16">
          <p className="text-sm text-[#3D2140]/60 mb-4">Vuoi ricevere altre idee per crescere con cura?</p>
          <Link to="/#newsletter" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#3D2140] text-white hover:bg-[#5A3660] transition-colors text-sm font-semibold">
            Iscriviti alla newsletter <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export { CATEGORY_LABELS };

/** Box "la casella di oggi ti aspetta": iscrizione newsletter con promemoria quotidiano */
function ReminderSignup() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("sending");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, name: "" });
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div className="max-w-xl mx-auto text-center mb-12 bg-[#81C3C9]/25 rounded-2xl px-6 py-5" data-testid="reminder-success">
        <p className="font-display text-xl text-[#3D2140]">Fatto! Ti aspetto ogni mattina.</p>
        <p className="text-sm text-[#3D2140]/70 mt-1">Dal 1° al 24 dicembre ti scriverò quando si apre la casella del giorno.</p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto text-center mb-12 bg-white/70 backdrop-blur rounded-2xl px-6 py-6 border border-[#FF2768]/20" data-testid="reminder-box">
      <p className="font-display text-xl md:text-2xl text-[#3D2140]">La casella di oggi ti aspetta</p>
      <p className="text-sm text-[#3D2140]/70 mt-1 mb-4">Lasciami la tua email: ogni mattina di dicembre ti ricordo quando si apre la casella del giorno.</p>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 justify-center">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="La tua email"
          data-testid="reminder-email"
          className="flex-1 px-4 py-3 rounded-full border border-[#3D2140]/15 bg-[#FAF8F5] text-[#3D2140] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2768]/40"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          data-testid="reminder-submit"
          className="px-6 py-3 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#c81e52] transition-colors disabled:opacity-50"
        >
          {state === "sending" ? "Un attimo…" : "Ricordamelo"}
        </button>
      </form>
      {state === "error" && <p className="text-xs text-[#FF2768] mt-2">Qualcosa non ha funzionato. Riprova tra poco.</p>}
    </div>
  );
}
